import {autorun, makeAutoObservable, runInAction, toJS} from "mobx"
import {
    GetAllSportObjectsDocument,
    GetSportObjectsByIdsDocument,
    SportObjectEntity, SportZoneEntity,
} from "../../graphql/graphQlApiHooks";
import RootStore from '../.';
import {isEqual, queryToMobxObservable, sortByCreateAt} from "../../helpers";
import Settings from "../settings";
import {client} from "../.";

const SportObjects = class {
    public rootStore?: RootStore;
    public current?: SportObjectEntity;
    public currentIds: string[] = [];
    public sportObjects: SportObjectEntity[] = [];
    public allSportObjects: SportObjectEntity[] = [];
    public loading: boolean = true
    public error?: any
    public count: number = 0
    // public userSportObjects: string[] | null = null;

    constructor(rootStore: RootStore) {
        this.rootStore = rootStore;
        makeAutoObservable(this)
        
        // Синхронное восстановление выбранных объектов из localStorage при инициализации
        try {
            const savedIds = localStorage.getItem('sportObjectIds');
            if (savedIds) {
                const parsedIds = JSON.parse(savedIds);
                if (Array.isArray(parsedIds) && parsedIds.length > 0) {
                    runInAction(() => {
                        this.currentIds = parsedIds;
                    });
                }
            }
        } catch (error) {
            console.error('Error restoring sportObjectIds from localStorage:', error);
        }
        
        autorun(()=>{
            if (this.querySportObjects?.current() ){
                let result: any = this.querySportObjects.current()
                result = result.getAllSportObjects.slice().sort(sortByCreateAt);

                let userSportObjects: SportObjectEntity[];
                if (this.userSportObjects !== null && this.userSportObjects.length > 0) {
                    let userSportObjectsIds: string[] = this.userSportObjects;
                    userSportObjects =  [...result].filter(({id}) => userSportObjectsIds.includes(id))
                } else {
                    userSportObjects = result
                }


                if ( !isEqual(result, toJS(this.allSportObjects)) ) {
                    this.allSportObjects = result
                }

                if ( !isEqual(userSportObjects, toJS(this.sportObjects)) ) {
                    runInAction(()=>{
                        this.sportObjects = userSportObjects;
                        this.count = this.sportObjects.length;
                        this.setLoading(false)
                    })

                    this.rootStore?.settingsStore.setSportObjects(userSportObjects);

                    if (this.sportObjects.length > 0 && !this.current && !localStorage.getItem('sportObjectId')) {
                        this.setCurrent(this.sportObjects[0].id)
                    }
                    if (localStorage.getItem('sportObjectId')) {
                        this.setCurrent(localStorage.getItem('sportObjectId') as string)
                    }
                    
                    // Восстанавливаем выбранные ID из localStorage, если они есть
                    // Или устанавливаем все доступные объекты, если сохраненных нет
                    const savedIds = localStorage.getItem('sportObjectIds');
                    if (savedIds) {
                        try {
                            const parsedIds = JSON.parse(savedIds);
                            if (Array.isArray(parsedIds) && parsedIds.length > 0) {
                                // Фильтруем сохраненные ID, оставляя только те, что доступны пользователю
                                const validIds = parsedIds.filter((id: string) => 
                                    this.sportObjects.find(obj => obj.id === id)
                                );
                                if (validIds.length > 0) {
                                    this.setCurrentIds(validIds);
                                } else if (this.sportObjects.length > 0) {
                                    // Если сохраненные ID невалидны, устанавливаем все доступные
                                    const sportObjectIds = [...this.sportObjects].map((obj:SportObjectEntity) => obj.id);
                                    this.setCurrentIds(sportObjectIds);
                                }
                            } else if (this.sportObjects.length > 0) {
                                // Если сохраненные ID пустые, устанавливаем все доступные
                                const sportObjectIds = [...this.sportObjects].map((obj:SportObjectEntity) => obj.id);
                                this.setCurrentIds(sportObjectIds);
                            }
                        } catch (error) {
                            console.error('Error parsing sportObjectIds from localStorage:', error);
                            // При ошибке парсинга устанавливаем все доступные объекты
                            if (this.sportObjects.length > 0) {
                                const sportObjectIds = [...this.sportObjects].map((obj:SportObjectEntity) => obj.id);
                                this.setCurrentIds(sportObjectIds);
                            }
                        }
                    } else if (this.sportObjects.length > 0) {
                        // Если сохраненных ID нет, устанавливаем все доступные объекты
                        const sportObjectIds = [...this.sportObjects].map((obj:SportObjectEntity) => obj.id);
                        this.setCurrentIds(sportObjectIds);
                    }
                }
                this.setLoading(false)
            }

        })
    }
    setLoading(loading: boolean){
        this.loading = loading
    }
    get userSportObjects(){
        const user_sportObjectsData = localStorage.getItem('user_sportObjects') as string
        return (user_sportObjectsData !== 'undefined') ?  JSON.parse(localStorage.getItem('user_sportObjects') as string) : []
    }

    get querySportObjects() {
        if ( this.rootStore?.userStore?.currentUser )
            return queryToMobxObservable(client.watchQuery({
                query: GetAllSportObjectsDocument,
                pollInterval: Settings.pollIntervals.allSportObjects,
            }));
        return undefined;
    }

    public setCurrent = async (sportObjectId: string) => {
        autorun(() => {
            this.rootStore?.sportZonesStore.fetchSportZones(sportObjectId)
        });

        runInAction(() =>  this.current = this.sportObjects.find((obj:SportObjectEntity) => obj.id === sportObjectId));

        localStorage.setItem('sportObjectId', sportObjectId)
    };

    public setSportObjects = (sportObjects: SportObjectEntity[]) => {
        this.sportObjects = sportObjects;
    };

    public setUserSportObjects = async (sportObjectIds: string[]) => {
        if (!sportObjectIds || sportObjectIds.length === 0) return;
        
        this.setLoading(true);
        
        try {
            // Загружаем полные объекты по ID
            const result = await client.query({
                query: GetSportObjectsByIdsDocument,
                variables: { ids: sportObjectIds },
                fetchPolicy: 'network-only'
            });
            
            const sportObjects = (result.data?.getSportObjects || []) as SportObjectEntity[];
            
            if (sportObjects.length === 0) {
                this.setLoading(false);
                return;
            }
            
            runInAction(() => {
                this.sportObjects = sportObjects.slice().sort(sortByCreateAt);
                this.count = this.sportObjects.length;
                this.setLoading(false);
            });

            this.rootStore?.settingsStore.setSportObjects(this.sportObjects);

            // Устанавливаем текущий объект если его нет
            if (this.sportObjects.length > 0 && !this.current && !localStorage.getItem('sportObjectId')) {
                this.setCurrent(this.sportObjects[0].id);
            }
            if (localStorage.getItem('sportObjectId')) {
                const savedId = localStorage.getItem('sportObjectId') as string;
                if (this.sportObjects.find(obj => obj.id === savedId)) {
                    this.setCurrent(savedId);
                }
            }
            
            // Устанавливаем текущие ID если их нет
            if (this.currentIds.length === 0 && !JSON.parse(localStorage.getItem('sportObjectIds') || 'null')) {
                const allIds = this.sportObjects.map((obj: SportObjectEntity) => obj.id);
                this.setCurrentIds(allIds);
            }
            if (JSON.parse(localStorage.getItem('sportObjectIds') || 'null')) {
                const savedIds = JSON.parse(localStorage.getItem('sportObjectIds') as string);
                // Фильтруем сохраненные ID, оставляя только те, что доступны пользователю
                const validIds = savedIds.filter((id: string) => 
                    this.sportObjects.find(obj => obj.id === id)
                );
                if (validIds.length > 0) {
                    this.setCurrentIds(validIds);
                } else if (this.sportObjects.length > 0) {
                    // Если сохраненные ID невалидны, устанавливаем все доступные
                    this.setCurrentIds(this.sportObjects.map((obj: SportObjectEntity) => obj.id));
                }
            }
        } catch (error) {
            console.error('Error loading user sport objects:', error);
            this.setLoading(false);
        }
    };

    public setCurrentIds = (ids: string[]) => {
        runInAction(() => this.currentIds = ids )
        localStorage.setItem('sportObjectIds', JSON.stringify(ids))

        let sportObjects = this.rootStore?.sportObjectsStore.sportObjects.filter((sportObject: SportObjectEntity) => ids.includes(sportObject.id));
        const sportZoneIds = [].concat.apply([], [...sportObjects.map((sportObject: SportObjectEntity) => sportObject.sportZones)]).map((sportZone: SportZoneEntity) => sportZone.id)
        this?.rootStore?.equipmentsStore.setFilters({sportZoneIds: sportZoneIds})

    };


};


export default SportObjects;
